/**
 * IDS workspace: inspect a client's specification, or author one from a form.
 *
 * The rule builder exists so a BIM coordinator who has never seen XML can still
 * express "all walls need a fire rating" and walk away with a standards-compliant
 * IDS file. The XML is shown, not hidden, because seeing it is what makes the
 * output trustworthy and portable.
 */

import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { LockRequestInput } from '../lib/api';
import type {
  AppConfig,
  IdsDocumentSummary,
  IdsInspection,
  IdsLockSummary,
  IdsSpecificationSummary,
  SimpleRuleDraft,
} from '../lib/types';
import { Dropzone } from './Dropzone';
import { LockRequestDialog } from './LockRequestDialog';
import { Badge, Callout, CloseIcon, DownloadIcon, PlusIcon, Spinner, formatDateTime } from './primitives';

const COMMON_ENTITIES = [
  'IFCWALL',
  'IFCSLAB',
  'IFCBEAM',
  'IFCCOLUMN',
  'IFCDOOR',
  'IFCWINDOW',
  'IFCSPACE',
  'IFCPIPESEGMENT',
  'IFCDUCTSEGMENT',
  'IFCMEMBER',
  'IFCPLATE',
  'IFCELEMENTASSEMBLY',
  'IFCBUILDINGELEMENTPROXY',
  'IFCCOVERING',
  'IFCRAILING',
  'IFCSTAIR',
  'IFCROOF',
  'IFCFOOTING',
  'IFCBRIDGE',
  'IFCEARTHWORKSFILL',
];

const DATA_TYPES = [
  'IFCLABEL',
  'IFCTEXT',
  'IFCBOOLEAN',
  'IFCINTEGER',
  'IFCREAL',
  'IFCLENGTHMEASURE',
  'IFCAREAMEASURE',
  'IFCVOLUMEMEASURE',
  'IFCIDENTIFIER',
];

function emptyRule(): SimpleRuleDraft {
  return {
    name: '',
    instructions: '',
    entity: 'IFCWALL',
    facet: 'property',
    property_set: 'Pset_WallCommon',
    property_name: 'FireRating',
    data_type: 'IFCLABEL',
    classification_system: 'Uniclass',
    attribute: 'Name',
    constraint: { type: 'present' },
    cardinality: 'required',
    optional_population: true,
  };
}

function describeRule(rule: SimpleRuleDraft): string {
  const target =
    rule.facet === 'property'
      ? `${rule.property_set}.${rule.property_name}`
      : rule.facet === 'classification'
        ? `classification${rule.classification_system ? ` (${rule.classification_system})` : ''}`
        : rule.facet === 'attribute'
          ? `attribute ${rule.attribute}`
          : 'material';

  const constraint = rule.constraint;
  let expectation = 'must be present';
  if (constraint.type === 'equals') expectation = `must equal ${constraint.value}`;
  else if (constraint.type === 'enumeration')
    expectation = `must be one of ${(constraint.values ?? []).join(', ')}`;
  else if (constraint.type === 'pattern') expectation = `must match /${constraint.pattern}/`;
  else if (constraint.type === 'range')
    expectation = `must be between ${constraint.min_value ?? '−∞'} and ${constraint.max_value ?? '∞'}`;

  const modality =
    rule.cardinality === 'prohibited'
      ? 'must NOT have'
      : rule.cardinality === 'optional'
        ? 'may have (but if present must be valid)'
        : 'must have';

  return `Every ${rule.entity} ${modality} ${target} — ${expectation}`;
}

interface IdsViewProps {
  config: AppConfig;
  documents: IdsDocumentSummary[];
  onDocumentsChanged: () => void;
}

export function IdsView({ config, documents, onDocumentsChanged }: IdsViewProps) {
  const [tab, setTab] = useState<'inspect' | 'build' | 'import' | 'saved'>('inspect');

  return (
    <div className="stack">
      <div className="card">
        <div className="card-body tight row">
          {(
            [
              ['inspect', 'Inspect an IDS'],
              ['build', 'Build a rule set'],
              ['import', 'Import from Excel'],
              ['saved', `Saved specifications${documents.length ? ` (${documents.length})` : ''}`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm${tab === key ? ' btn-primary' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'inspect' && <InspectPanel config={config} />}
      {tab === 'build' && (
        <BuildPanel config={config} onSaved={onDocumentsChanged} />
      )}
      {tab === 'import' && (
        <ImportPanel
          config={config}
          onSaved={() => {
            onDocumentsChanged();
            setTab('saved');
          }}
        />
      )}
      {tab === 'saved' && (
        <SavedPanel config={config} documents={documents} onChanged={onDocumentsChanged} />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ import
function ImportPanel({ config, onSaved }: { config: AppConfig; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canBuild = config.features.rule_builder;

  async function importSheet() {
    const file = files[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.importIdsFromExcel(file, name.trim() || file.name.replace(/\.xlsx?$/i, ''));
      onSaved();
      setFiles([]);
      setName('');
      void result;
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'The spreadsheet could not be imported.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (!canBuild) {
    return (
      <div className="card">
        <div className="card-body">
          <Callout variant="upsell">
            <strong>Importing rules from a spreadsheet is a Professional feature.</strong>
            <div style={{ marginTop: '0.2rem' }}>
              Write requirements as rows in Excel instead of the form or hand-written XML — it
              goes through the same builder as the rule-builder form.
            </div>
          </Callout>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <h2>Import from Excel</h2>
          <div className="masthead-spacer" />
          <span className="hint">One row per rule</span>
        </div>
        <div className="card-body stack">
          <p className="hint">
            First sheet, first row headers. Expected columns: Rule Name, Entity, Predefined Type,
            Facet, Property Set, Property/Attribute Name, Data Type, Constraint Type, Constraint
            Value, Min Value, Max Value, Cardinality, Applies To All, Instructions. Only Entity is
            required on every row — everything else falls back to a sensible default.
          </p>
          <label className="field">
            <span>Rule set name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Defaults to the file name"
            />
          </label>
          <Dropzone
            files={files}
            onChange={setFiles}
            accept={['.xlsx']}
            label="Drop a .xlsx rule sheet here"
            hint="Parsed straight into a saved IDS document you can lock or validate against."
          />
          {busy && (
            <div className="row">
              <Spinner />
              <span className="hint-strong">Importing…</span>
            </div>
          )}
          {error && <Callout variant="error">{error}</Callout>}
          <div className="row">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || files.length === 0}
              onClick={importSheet}
            >
              Import and save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- inspect
function InspectPanel({ config }: { config: AppConfig }) {
  const [files, setFiles] = useState<File[]>([]);
  const [inspection, setInspection] = useState<IdsInspection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function inspect(next: File[]) {
    setFiles(next);
    if (!next.length) return;
    setBusy(true);
    setError(null);
    try {
      setInspection(await api.inspectIds(next[0] as File));
    } catch (caught) {
      setInspection(null);
      setError(caught instanceof ApiError ? caught.message : 'The IDS file could not be read.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <h2>Inspect an IDS file</h2>
          <div className="masthead-spacer" />
          <span className="hint">Free on every plan</span>
        </div>
        <div className="card-body stack">
          <Dropzone
            files={files}
            onChange={inspect}
            accept={['.ids', '.xml', '.idsx']}
            label="Drop a buildingSMART IDS file here"
            hint="See exactly what a client's specification requires before you validate against it — including a locked .idsx, which verifies without revealing its rules unless you're signed in as whoever locked it."
          />
          {busy && (
            <div className="row">
              <Spinner />
              <span className="hint-strong">Reading specification…</span>
            </div>
          )}
          {error && <Callout variant="error">{error}</Callout>}
        </div>
      </div>

      {inspection && inspection.content_visible === false && (
        <IdsxProvenancePanel inspection={inspection} />
      )}

      {inspection && inspection.content_visible !== false && (
        <>
          <div className="card">
            <div className="card-head">
              <h3>{inspection.title}</h3>
              <div className="masthead-spacer" />
              <Badge>{inspection.specifications?.length ?? 0} specifications</Badge>
            </div>
            <div className="card-body">
              {inspection.idsx && <IdsxProvenancePanel inspection={inspection} inline />}
              <div className="meta-list">
                {[
                  ['Author', inspection.author],
                  ['Version', inspection.version],
                  ['Date', inspection.date],
                  ['Purpose', inspection.purpose],
                  ['Milestone', inspection.milestone],
                  ['Description', inspection.description],
                ]
                  .filter(([, value]) => value)
                  .map(([key, value]) => (
                    <div className="meta-row" key={key}>
                      <span className="meta-key">{key}</span>
                      <span className="meta-value">{value}</span>
                    </div>
                  ))}
              </div>
              {(inspection.warnings?.length ?? 0) > 0 && (
                <div style={{ marginTop: '0.9rem' }}>
                  <Callout variant="warn">
                    <strong>Parts of this file could not be interpreted:</strong>
                    <ul style={{ margin: '0.3rem 0 0 1rem', padding: 0 }}>
                      {inspection.warnings!.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </Callout>
                </div>
              )}
              {!config.features.ids_validation && (
                <div style={{ marginTop: '0.9rem' }}>
                  <Callout variant="upsell">
                    You can read this specification on the free tier. Validating a model
                    against it is a Professional feature.
                  </Callout>
                </div>
              )}
            </div>
          </div>

          {inspection.specifications?.map((specification, index) => (
            <SpecificationCard key={index} specification={specification} index={index} />
          ))}
        </>
      )}
    </div>
  );
}

/** Shown for every locked .idsx, whether or not its rules are visible --
 * the provenance itself (genuine, who locked it, when, for what) is the
 * useful thing to see either way. When `inline`, it's embedded inside the
 * already-visible specification card instead of standing alone. */
function IdsxProvenancePanel({ inspection, inline }: { inspection: IdsInspection; inline?: boolean }) {
  const requestedBy = [inspection.requested_by_name, inspection.requested_by_email]
    .filter(Boolean)
    .join(' — ');
  const rows: [string, string | undefined][] = [
    ['Rule set', inspection.name],
    ['Locked by', inspection.locked_by],
    ['Locked at', inspection.locked_at ? formatDateTime(inspection.locked_at) : undefined],
    ['Requested by', requestedBy || undefined],
    ['Project', inspection.project_name],
    ['Comments', inspection.comments],
    ['SHA-256', inspection.ids_sha256],
  ];

  const body = (
    <div className="meta-list">
      {rows
        .filter(([, value]) => value)
        .map(([key, value]) => (
          <div className="meta-row" key={key}>
            <span className="meta-key">{key}</span>
            <span className="meta-value">{value}</span>
          </div>
        ))}
    </div>
  );

  if (inline) {
    return (
      <div style={{ marginBottom: '0.9rem' }}>
        <Callout variant="info">
          <strong>Verified locked rule set — signed into the file, unedited since locking.</strong>
        </Callout>
        <div style={{ marginTop: '0.6rem' }}>{body}</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Verified locked rule set</h3>
      </div>
      <div className="card-body stack">
        <Callout variant="info">
          Genuine and unedited since locking — but its rules stay unreadable here unless you're
          signed in as the account that locked it. That's the same confidentiality guarantee
          IDSx makes everywhere: it can still be validated against without ever being read.
        </Callout>
        {body}
      </div>
    </div>
  );
}

function SpecificationCard({
  specification,
  index,
}: {
  specification: IdsSpecificationSummary;
  index: number;
}) {
  const cardinality =
    specification.max_occurs === 0
      ? 'Prohibited — these elements must not exist'
      : specification.min_occurs === 0
        ? 'Optional — no failure if no such elements exist'
        : `Required — at least ${specification.min_occurs} must exist`;

  return (
    <div className="card">
      <div className="card-head">
        <h3>
          {index + 1}. {specification.name}
        </h3>
        <div className="masthead-spacer" />
        <Badge>{cardinality}</Badge>
      </div>
      <div className="card-body stack" style={{ gap: '0.8rem' }}>
        {specification.description && <p className="hint">{specification.description}</p>}
        <div>
          <div className="field-label">Applies to</div>
          <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
            {specification.applicability.map((facet, i) => (
              <li key={i} className="hint">
                {facet.description}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="field-label">Requires</div>
          {specification.requirements.length === 0 ? (
            <p className="hint">Nothing beyond existence.</p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {specification.requirements.map((facet, i) => (
                <li key={i} className="hint">
                  <strong style={{ color: 'var(--ink)' }}>{facet.cardinality}</strong>:{' '}
                  {facet.description}
                  {facet.instructions ? ` — ${facet.instructions}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
        {specification.instructions && (
          <Callout>
            <strong>Author's instructions:</strong> {specification.instructions}
          </Callout>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------- build
function BuildPanel({ config, onSaved }: { config: AppConfig; onSaved: () => void }) {
  const [title, setTitle] = useState('Project information requirements');
  const [author, setAuthor] = useState('');
  const [ifcVersion, setIfcVersion] = useState('IFC4');
  const [rules, setRules] = useState<SimpleRuleDraft[]>([emptyRule()]);
  const [xml, setXml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveName, setSaveName] = useState('');

  const canBuild = config.features.rule_builder;

  function update(index: number, patch: Partial<SimpleRuleDraft>) {
    setRules((current) =>
      current.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    );
  }

  async function build(save: boolean) {
    setBusy(true);
    setError(null);
    try {
      const named = rules.map((rule, index) => ({
        ...rule,
        name: rule.name.trim() || `Rule ${index + 1}`,
      }));
      const result = await api.buildIds({
        info: { title, author, ifc_version: ifcVersion },
        rules: named,
        save_as: save ? saveName || title : null,
      });
      setXml(result.xml);
      if (save) {
        onSaved();
      } else {
        api.saveBlob(new Blob([result.xml], { type: 'application/xml' }), result.filename);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The rule set could not be built.');
    } finally {
      setBusy(false);
    }
  }

  if (!canBuild) {
    return (
      <div className="card">
        <div className="card-body">
          <Callout variant="upsell">
            <strong>The rule builder is a Professional feature.</strong>
            <div style={{ marginTop: '0.2rem' }}>
              It turns plain-language requirements into a valid buildingSMART IDS file you can
              re-use, issue to your suppliers, or take to any other IDS-capable tool.
            </div>
          </Callout>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <h2>Rule set details</h2>
        </div>
        <div className="card-body">
          <div className="form-grid cols-3">
            <label className="field">
              <span>Title</span>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <label className="field">
              <span>Author</span>
              <input
                type="text"
                value={author}
                placeholder="you@yourfirm.com"
                onChange={(e) => setAuthor(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Target IFC version</span>
              <select value={ifcVersion} onChange={(e) => setIfcVersion(e.target.value)}>
                <option value="IFC2X3">IFC2X3</option>
                <option value="IFC4">IFC4</option>
                <option value="IFC4X3_ADD2">IFC4X3</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Rules</h2>
          <div className="masthead-spacer" />
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setRules((current) => [...current, emptyRule()])}
          >
            <PlusIcon /> Add rule
          </button>
        </div>
        <div className="card-body stack">
          {rules.map((rule, index) => (
            <RuleEditor
              key={index}
              rule={rule}
              index={index}
              onChange={(patch) => update(index, patch)}
              onRemove={
                rules.length > 1
                  ? () => setRules((current) => current.filter((_, i) => i !== index))
                  : undefined
              }
            />
          ))}

          {error && <Callout variant="error">{error}</Callout>}

          <div className="row">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => build(false)}
              disabled={busy}
            >
              <DownloadIcon /> {busy ? 'Building…' : 'Build and download .ids'}
            </button>
            <input
              type="text"
              style={{ maxWidth: '18rem' }}
              placeholder="Save as (name)"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
            />
            <button type="button" className="btn" onClick={() => build(true)} disabled={busy}>
              Save for re-use
            </button>
          </div>
        </div>
      </div>

      {xml && (
        <div className="card">
          <div className="card-head">
            <h3>Generated IDS</h3>
            <div className="masthead-spacer" />
            <span className="hint">
              Standards-compliant XML — usable in any other IDS-capable tool
            </span>
          </div>
          <div className="card-body">
            <pre className="xml-preview">{xml}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function RuleEditor({
  rule,
  index,
  onChange,
  onRemove,
}: {
  rule: SimpleRuleDraft;
  index: number;
  onChange: (patch: Partial<SimpleRuleDraft>) => void;
  onRemove?: () => void;
}) {
  const constraint = rule.constraint;

  return (
    <div className="rule">
      <div className="rule-head">
        <div className="check-grow">
          <input
            type="text"
            value={rule.name}
            placeholder={`Rule ${index + 1} name, e.g. "Walls carry a fire rating"`}
            onChange={(e) => onChange({ name: e.target.value })}
          />
          <div className="rule-summary">{describeRule(rule)}</div>
        </div>
        {onRemove && (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={onRemove}
            aria-label={`Remove rule ${index + 1}`}
          >
            <CloseIcon />
          </button>
        )}
      </div>

      <div className="form-grid cols-3" style={{ marginTop: '0.7rem' }}>
        <label className="field">
          <span>Applies to</span>
          <input
            type="text"
            list="clearifc-entities"
            value={rule.entity}
            onChange={(e) => onChange({ entity: e.target.value.toUpperCase() })}
          />
        </label>
        <label className="field">
          <span>Requirement type</span>
          <select
            value={rule.facet}
            onChange={(e) => onChange({ facet: e.target.value as SimpleRuleDraft['facet'] })}
          >
            <option value="property">Property in a property set</option>
            <option value="classification">Classification</option>
            <option value="attribute">IFC attribute</option>
            <option value="material">Material</option>
          </select>
        </label>
        <label className="field">
          <span>Cardinality</span>
          <select
            value={rule.cardinality}
            onChange={(e) =>
              onChange({ cardinality: e.target.value as SimpleRuleDraft['cardinality'] })
            }
          >
            <option value="required">Required</option>
            <option value="optional">Optional (valid if present)</option>
            <option value="prohibited">Prohibited</option>
          </select>
        </label>
      </div>

      {rule.facet === 'property' && (
        <div className="form-grid cols-3">
          <label className="field">
            <span>Property set</span>
            <input
              type="text"
              value={rule.property_set ?? ''}
              placeholder="Pset_WallCommon"
              onChange={(e) => onChange({ property_set: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Property</span>
            <input
              type="text"
              value={rule.property_name ?? ''}
              placeholder="FireRating"
              onChange={(e) => onChange({ property_name: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Data type</span>
            <select
              value={rule.data_type ?? 'IFCLABEL'}
              onChange={(e) => onChange({ data_type: e.target.value })}
            >
              {DATA_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {rule.facet === 'classification' && (
        <label className="field">
          <span>Classification system</span>
          <input
            type="text"
            value={rule.classification_system ?? ''}
            placeholder="Uniclass"
            onChange={(e) => onChange({ classification_system: e.target.value })}
          />
        </label>
      )}

      {rule.facet === 'attribute' && (
        <label className="field">
          <span>Attribute name</span>
          <input
            type="text"
            value={rule.attribute ?? ''}
            placeholder="Name"
            onChange={(e) => onChange({ attribute: e.target.value })}
          />
        </label>
      )}

      <div className="form-grid cols-2">
        <label className="field">
          <span>Value constraint</span>
          <select
            value={constraint.type}
            onChange={(e) =>
              onChange({
                constraint: { type: e.target.value as SimpleRuleDraft['constraint']['type'] },
              })
            }
          >
            <option value="present">Present (any value)</option>
            <option value="equals">Equals a specific value</option>
            <option value="enumeration">One of a list</option>
            <option value="pattern">Matches a pattern</option>
            <option value="range">Within a numeric range</option>
          </select>
        </label>

        {constraint.type === 'equals' && (
          <label className="field">
            <span>Value</span>
            <input
              type="text"
              value={constraint.value ?? ''}
              onChange={(e) => onChange({ constraint: { ...constraint, value: e.target.value } })}
            />
          </label>
        )}

        {constraint.type === 'enumeration' && (
          <label className="field">
            <span>Allowed values (comma separated)</span>
            <input
              type="text"
              value={(constraint.values ?? []).join(', ')}
              placeholder="-/30/30, -/60/60, -/120/120"
              onChange={(e) =>
                onChange({
                  constraint: {
                    ...constraint,
                    values: e.target.value
                      .split(',')
                      .map((value) => value.trim())
                      .filter(Boolean),
                  },
                })
              }
            />
          </label>
        )}

        {constraint.type === 'pattern' && (
          <label className="field">
            <span>Regular expression</span>
            <input
              type="text"
              value={constraint.pattern ?? ''}
              placeholder="Ss_\\d{2}(_\\d{2})*"
              onChange={(e) =>
                onChange({ constraint: { ...constraint, pattern: e.target.value } })
              }
            />
          </label>
        )}

        {constraint.type === 'range' && (
          <div className="form-grid cols-2">
            <label className="field">
              <span>Minimum</span>
              <input
                type="number"
                value={constraint.min_value ?? ''}
                onChange={(e) =>
                  onChange({
                    constraint: {
                      ...constraint,
                      min_value: e.target.value === '' ? null : Number(e.target.value),
                    },
                  })
                }
              />
            </label>
            <label className="field">
              <span>Maximum</span>
              <input
                type="number"
                value={constraint.max_value ?? ''}
                onChange={(e) =>
                  onChange({
                    constraint: {
                      ...constraint,
                      max_value: e.target.value === '' ? null : Number(e.target.value),
                    },
                  })
                }
              />
            </label>
          </div>
        )}
      </div>

      <label className="field" style={{ marginBottom: 0 }}>
        <span>Instructions shown when this rule fails</span>
        <input
          type="text"
          value={rule.instructions ?? ''}
          placeholder="e.g. Fire ratings come from the fire engineer's schedule, revision C."
          onChange={(e) => onChange({ instructions: e.target.value })}
        />
      </label>

      <datalist id="clearifc-entities">
        {COMMON_ENTITIES.map((entity) => (
          <option key={entity} value={entity} />
        ))}
      </datalist>
    </div>
  );
}

// --------------------------------------------------------------------- saved
function SavedPanel({
  config,
  documents,
  onChanged,
}: {
  config: AppConfig;
  documents: IdsDocumentSummary[];
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The document currently being locked -- opens <LockRequestDialog> for it.
  // Not a boolean: keeping the whole document means the dialog can show its
  // name without a second lookup.
  const [lockTarget, setLockTarget] = useState<IdsDocumentSummary | null>(null);
  // Bumped after a successful lock so <LocksPanel> (which loads its own
  // list on mount) remounts and picks up the new lock immediately, instead
  // of the admin having to hit its own Refresh button.
  const [locksVersion, setLocksVersion] = useState(0);

  if (!config.features.ids_validation) {
    return (
      <div className="card">
        <div className="card-body">
          <Callout variant="upsell">
            Saving specifications per client or project is a Professional feature.
          </Callout>
        </div>
      </div>
    );
  }

  async function lock(document: IdsDocumentSummary, request: LockRequestInput) {
    setError(null);
    setNotice(null);
    const { filename } = await api.lockIdsDocument(document.id, request);
    setLockTarget(null);
    setNotice(
      `${filename} downloaded. Send it to your contractor — their validation report will ` +
        `show it came from this exact, unedited rule set.`,
    );
    setLocksVersion((v) => v + 1);
  }

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <h2>Saved specifications</h2>
          <div className="masthead-spacer" />
          <span className="hint">Re-usable per client or project</span>
        </div>
        {error && (
          <div className="card-body tight">
            <Callout variant="error">{error}</Callout>
          </div>
        )}
        {notice && (
          <div className="card-body tight">
            <Callout variant="info">{notice}</Callout>
          </div>
        )}
        {!config.features.ids_locking && (
          <div className="card-body tight">
            <Callout variant="upsell">
              <strong>Locked, encrypted IDS files (.idsx) are an Enterprise feature.</strong>
              <div style={{ marginTop: '0.2rem' }}>
                Lock a rule set so a contractor's validation report can prove it was checked
                against your exact, unedited requirements — and so the rules themselves stay
                unreadable to anyone who opens the file directly.
              </div>
            </Callout>
          </div>
        )}
        {documents.length === 0 ? (
          <div className="empty">
            <p>No saved specifications yet.</p>
            <p className="hint">
              Build a rule set, or save a client's IDS file, and it will be selectable whenever
              you validate.
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Specifications</th>
                  <th>Saved</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {documents.map((document) => (
                  <tr key={document.id}>
                    <td>
                      <div className="meta-value">{document.name}</div>
                      {document.description && (
                        <div className="hint">{document.description}</div>
                      )}
                    </td>
                    <td>{document.specification_count}</td>
                    <td className="cell-mono">{document.created_at?.slice(0, 10) ?? '—'}</td>
                    <td>
                      <div className="row">
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => api.downloadIdsDocument(document.id)}
                        >
                          <DownloadIcon /> Export
                        </button>
                        {config.features.ids_locking && (
                          <button
                            type="button"
                            className="btn btn-sm btn-accent"
                            onClick={() => setLockTarget(document)}
                            aria-label={`Lock ${document.name} into a signed .idsx`}
                          >
                            Lock (.idsx)
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={async () => {
                            try {
                              await api.deleteIdsDocument(document.id);
                              onChanged();
                            } catch (caught) {
                              setError(
                                caught instanceof ApiError
                                  ? caught.message
                                  : 'Could not delete that specification.',
                              );
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {config.features.ids_locking && <LocksPanel key={locksVersion} />}

      {lockTarget && (
        <LockRequestDialog
          documentName={lockTarget.name}
          onConfirm={(request) => lock(lockTarget, request)}
          onCancel={() => setLockTarget(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------- locks
function LocksPanel() {
  const [locks, setLocks] = useState<IdsLockSummary[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  async function load() {
    setBusy(true);
    setError(null);
    try {
      const body = await api.listIdsLocks();
      setLocks(body.locks);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load issued locks.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (busy && locks.length === 0 && !error) return null;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Issued locks</h2>
        <div className="masthead-spacer" />
        <button type="button" className="btn btn-sm" onClick={() => void load()} disabled={busy}>
          Refresh
        </button>
      </div>
      {error && (
        <div className="card-body tight">
          <Callout variant="error">{error}</Callout>
        </div>
      )}
      {locks.length === 0 ? (
        <div className="card-body tight">
          <p className="hint">No .idsx files locked yet.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Rule set</th>
                <th>SHA-256</th>
                <th>Locked</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {locks.map((lock) => (
                <tr key={lock.lock_id}>
                  <td>{lock.name}</td>
                  <td className="cell-mono">{lock.ids_sha256.slice(0, 12)}…</td>
                  <td className="cell-mono">{lock.created_at?.slice(0, 10) ?? '—'}</td>
                  <td>{lock.revoked ? 'Revoked' : 'Active'}</td>
                  <td>
                    {!lock.revoked && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        disabled={revoking === lock.lock_id}
                        onClick={async () => {
                          setRevoking(lock.lock_id);
                          setError(null);
                          try {
                            await api.revokeIdsLock(lock.lock_id);
                            await load();
                          } catch (caught) {
                            setError(
                              caught instanceof ApiError
                                ? caught.message
                                : 'Could not revoke that lock.',
                            );
                          } finally {
                            setRevoking(null);
                          }
                        }}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
